import React, { useState, useCallback } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Play, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PersonalInfoForm } from "@/components/Calculator/PersonalInfoForm";
import { useCalculatorState } from "@/components/Calculator/hooks/useCalculatorState";

interface HeroProps {
  scrollToCalculator: () => void;
}

const LazyVideo: React.FC = () => {
  const [playing, setPlaying] = useState(false);
  const handlePlay = useCallback(() => setPlaying(true), []);

  if (playing) {
    return (
      <div className="my-4 rounded-xl overflow-hidden shadow-md">
        <video
          src="/videos/video-tax.mp4"
          autoPlay
          muted
          loop
          playsInline
          controls
          className="w-full h-auto"
          aria-label="Vídeo explicativo da TaxHealth"
        />
      </div>
    );
  }

  return (
    <div
      className="my-4 rounded-xl overflow-hidden shadow-md relative cursor-pointer aspect-video flex items-center justify-center"
      onClick={handlePlay}
      role="button"
      aria-label="Reproduzir vídeo explicativo da TaxHealth"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handlePlay()}
    >
      <img
        src="/images/video-thumb.png"
        alt="Thumbnail do vídeo explicativo da TaxHealth"
        className="absolute inset-0 w-full h-full object-cover"
        width={640}
        height={360}
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      <div className="relative z-10 bg-primary/90 rounded-full p-4 shadow-lg hover:bg-primary transition-colors">
        <Play className="h-8 w-8 text-primary-foreground fill-current" />
      </div>
      <p className="absolute bottom-3 left-0 right-0 text-center text-sm text-white font-medium z-10">
        Clique para assistir
      </p>
    </div>
  );
};

const Hero: React.FC<HeroProps> = ({ scrollToCalculator }) => {
  const [showForm, setShowForm] = useState(false);
  const {
    formData,
    isSubmitting,
    actions
  } = useCalculatorState();
  return (
    <section className="relative w-full px-4 md:px-6 pt-6 md:pt-16 pb-12 md:pb-24 overflow-hidden bg-gradient-to-b from-accent to-background overflow-x-hidden">
      <div className="absolute inset-0 opacity-40 bg-hero-pattern" aria-hidden="true"></div>
      <div className="container relative max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-10 items-center">
          <div className="flex flex-col max-w-2xl mx-auto text-center md:text-left space-y-4">
            <div className="animate-fade-in">
              <h1 className="text-xl md:text-2xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
                VOCÊ É MÉDICO, LABORATÓRIO, CLÍNICA OU HOSPITAL E OPERA EM <span className="text-primary">LUCRO PRESUMIDO</span>?
              </h1>
            </div>
            <div className="mt-3 md:mt-4 animate-fade-up space-y-4">
              <p className="text-lg md:text-xl font-semibold text-foreground">
                Aumente seu lucro e reduza sua carga tributária com a Tax Health
              </p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Entenda como uma inteligência tributária focada em profissionais da saúde pode, usando processos administrativos, te ajudar a lucrar mais, economizando e restituindo impostos.
              </p>
              
              {/* Vídeo explicativo TaxHealth - lazy loaded */}
              <LazyVideo />
            </div>
          </div>

          <div className="w-full">
            <Card className="shadow-lg overflow-hidden">
              <CardContent className="pt-6 p-4 md:p-6">
                {!showForm ? (
                  <>
                    <div className="bg-gradient-to-r from-[hsl(142,72%,45%)] to-[hsl(142,72%,55%)] text-white rounded-t-lg -mx-4 md:-mx-6 -mt-6 px-4 md:px-6 py-3 md:py-4 mb-4">
                      <h2 className="text-sm md:text-lg font-semibold break-words">Verifique sua elegibilidade agora no WhatsApp!</h2>
                    </div>
                    <div className="flex flex-col items-center gap-4 py-6">
                      <p className="text-center text-muted-foreground">
                        Descubra como economizar nos impostos da sua clínica ou consultório.
                      </p>
                      <Button
                        onClick={() => setShowForm(true)}
                        className="w-full min-h-[56px] bg-[hsl(142,72%,45%)] hover:bg-[hsl(142,72%,38%)] text-white font-semibold text-sm md:text-lg whitespace-normal"
                      >
                        <MessageCircle className="w-6 h-6 mr-2" />
                        Fale com nosso time agora no WhatsApp!
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-gradient-to-r from-[hsl(142,72%,45%)] to-[hsl(142,72%,55%)] text-white rounded-t-lg -mx-4 md:-mx-6 -mt-6 px-4 md:px-6 py-3 md:py-4 mb-4">
                      <h2 className="text-sm md:text-lg font-semibold break-words">Preencha para falar no WhatsApp</h2>
                    </div>
                    <PersonalInfoForm 
                      formData={formData} 
                      onChange={actions.handleChange} 
                      onSubmit={actions.handleSubmit} 
                      isSubmitting={isSubmitting} 
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;